import { Box, Card, Container, Divider, inputBaseClasses, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import OtpInput from 'react-otp-input';
import { Authentication_Verification_Type_Enum } from '@kheops/graphql';
import KheopsLogo from '../assets/logo/kheopsLogo.svg?react';
import EmailSentImage from '../assets/photos/email-sent.png';
import { useRedirectIfAuth } from '../hooks/useRedirectIfAuth';
import useAuth from '../hooks/useAuth/useAuth';
import { RoutePaths } from '../routes/AppRoutes';
import { preLoginEmailAtom } from './state';

const errorsMap = new Map<Authentication_Verification_Type_Enum, string>([
  [Authentication_Verification_Type_Enum.NoLongerValid, 'login:authentication_code_no_longer_valid'],
  [Authentication_Verification_Type_Enum.Invalid, 'login:authentication_code_invalid'],
]);

export default function PreLoginPage(): React.JSX.Element {
  const RedirectIfAuth = useRedirectIfAuth();
  const { loginWithAuthCode, errors } = useAuth();
  const { t } = useTranslation(['common', 'login']);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const preLoginEmail = useAtomValue(preLoginEmailAtom);
  const [enableErrorDisplay, setEnableErrorDisplay] = useState(false);
  const [otp, setOtp] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!preLoginEmail) {
      navigate(RoutePaths.LOGIN);
    }
  }, [preLoginEmail]);

  useEffect(() => {
    if (otp.length === 6) {
      setEnableErrorDisplay(true);
      loginWithAuthCode(otp, preLoginEmail);
      return;
    }
    setEnableErrorDisplay(false);
  }, [otp]);

  const loginError = useMemo(() => {
    return (enableErrorDisplay && errors?.length) ? errors[0] as Authentication_Verification_Type_Enum : undefined;
  }, [errors, enableErrorDisplay]);

  return (
    <RedirectIfAuth>
      <Container
        maxWidth="sm"
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            width: {
              xs: 364,
              sm: 500,
            },
            textAlign: 'center',
            p: 0,
          }}
        >
          <Box
            component="img"
            src={EmailSentImage}
            sx={{
              width: '100%',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              px: {
                xs: 3,
                sm: 7,
              },
              pb: {
                xs: 3,
                sm: 7,
              },
              mt: 2,
            }}
          >
            <Box
              component={KheopsLogo}
              sx={{
                height: 46,
                width: 172,
              }}
            />
            <Typography variant="titleLarge">{t('login:confirm_identity')}</Typography>
            <Typography variant="bodyMedium">{t('login:magic_link_sent_confirmation', { preLoginEmail })}</Typography>
            <Typography variant="titleMedium" align="center">{t('login:enter_code_below')}</Typography>
            <OtpInput
              onChange={setOtp}
              value={otp}
              containerStyle={{
                gap: isXs ? '8px' : '16px',
                width: '100%',
              }}
              inputType="number"
              numInputs={6}
              renderInput={(props) => (
                <TextField
                  type="number"
                  sx={{
                    flex: 1,
                    [`& .${inputBaseClasses.root}`]: {
                      justifyContent: 'center',
                      p: 0,
                    },
                  }}
                  slotProps={{
                    htmlInput: {
                      ...props,
                    },
                  }}
                />
              )}
            />
            {loginError && (
              <Typography variant="bodySmall" color="error">
                <Trans
                  i18nKey={errorsMap.get(loginError) || loginError}
                  components={{
                    link1: <Link to={RoutePaths.LOGIN} style={{ color: 'inherit' }} />,
                  }}
                />
              </Typography>
            )}
            <Divider sx={{ width: '100%' }}>
              <Typography variant="bodyMedium">{t('common:or')}</Typography>
            </Divider>
            <Typography variant="bodyLarge">{t('login:click_link_sent_by_email')}</Typography>
          </Box>
        </Card>
      </Container>
    </RedirectIfAuth>
  );
}
